import { AxiosResponse } from 'axios';
import $api from '@/http/index';
import { IUser } from '@models/IUser';
import { IUserAvatar } from '@models/IUserAvatar';
import { IAddressBookItem } from '@models/addressbook/IAddressBookItem';
import { IUserAssistants } from '@models/IUserAssistants';
import { IReplacingPerson } from '@models/IReplacingPerson';
import {
    IApprovalAssistantsData,
    IChangeApprovalAssistantsData,
} from '@organisms/ApprovalAssistants/IApprovalAssistantsData';
import { INewUser, IUsersManagementData } from '@organisms/UsersManagement/IUsersManagementData';
import { IChangeUserProfileData } from '@organisms/UserProfile/IChangeUserProfileData';
import { IChangePasswordData } from '@organisms/ProfileSecurity/IChangePasswordData';
import { IChangeOrganizationProfileData } from '@organisms/OrganizationProfile/IChangeOrganizationProfileData';
import { ICustomUserSettingsForm } from '@models/customUserSettings/ICustomUserSettingsForm';
import { ICustomUserSettingsData } from '@models/customUserSettings/ICustomUserSettingsData';

export class UserService {
    static async getCurrentUser(): Promise<AxiosResponse<IUser>> {
        return $api.get<IUser>(`/user/profile`);
    }

    static async getCurrentUserAvatar(): Promise<AxiosResponse<IUserAvatar>> {
        return $api.get<IUserAvatar>(`/user/avatar`);
    }

    static async setAvatar(file: File): Promise<AxiosResponse> {
        const data = new FormData();
        data.append('avatar', file);
        return $api.post(`/user/avatar`, data, { timeout: 60000 });
    }

    static async deleteAvatar(): Promise<AxiosResponse> {
        return $api.delete(`/user/avatar`, { timeout: 15000 });
    }

    static async changePassword(formData: IChangePasswordData): Promise<AxiosResponse> {
        return $api.post(`/user/ChangePassword`, formData);
    }

    static async changeProfile(formData: IChangeUserProfileData): Promise<AxiosResponse> {
        return $api.post(`/user/ChangeProfile`, formData);
    }

    static async getDepsToSelect(): Promise<AxiosResponse<IAddressBookItem[]>> {
        return $api.get<IAddressBookItem[]>(`/addressBook/getDepartments`);
    }

    static async fetchCustomUserSettingsForm(id: string): Promise<AxiosResponse<ICustomUserSettingsForm>> {
        return $api.get<ICustomUserSettingsForm>(`/user/customusersettings/scheme/${id}`);
    }

    static async fetchCustomUserSettingsData(id: string): Promise<AxiosResponse<ICustomUserSettingsData>> {
        return $api.get<ICustomUserSettingsData>(`/user/customusersettings/data/${id}`);
    }
    static async setCustomUserSettingsData(id: string, dto: ICustomUserSettingsData): Promise<AxiosResponse> {
        return $api.post(`/user/customusersettings/data/${id}`, dto);
    }

    static async setUserDepartment(depId: string): Promise<AxiosResponse> {
        const data = new FormData();
        data.append('depId', depId);
        return $api.post<IAddressBookItem[]>(`/addressBook/setUserDepartment`, data);
    }

    static async fetchUserOrganization(
        controller: AbortController,
    ): Promise<AxiosResponse<IChangeOrganizationProfileData>> {
        return $api.get<IChangeOrganizationProfileData>(`/user/userOrganization`, { signal: controller.signal });
    }

    static async changeUserOrganization(formData: IChangeOrganizationProfileData): Promise<AxiosResponse> {
        return $api.post<IChangeOrganizationProfileData>(`/user/userOrganization`, formData);
    }

    static async fetchUsersForActivation(signal: AbortSignal): Promise<AxiosResponse<INewUser[]>> {
        // отказ показывает таблица (плитка с «Повторить») — общий тост был бы вторым сообщением
        return $api.get<INewUser[]>(`/user/UsersForActivation`, { signal, suppressErrorNotification: true });
    }

    static async approveActivation(formData: IUsersManagementData): Promise<AxiosResponse> {
        return $api.post(`/user/ApproveActivation`, formData);
    }

    static async lockUser(formData: IUsersManagementData): Promise<AxiosResponse> {
        return $api.post(`/user/LockUser`, formData);
    }

    static async unlockUser(formData: IUsersManagementData): Promise<AxiosResponse> {
        return $api.post(`/user/UnlockUser`, formData);
    }

    static async setAssistant(formData: IUserAssistants): Promise<AxiosResponse> {
        return $api.post(`/user/Assistants`, formData);
    }

    static async getAssistant(): Promise<AxiosResponse<IUserAssistants>> {
        return $api.get<IUserAssistants>(`/user/Assistants`);
    }

    /** Список людей, которых текущий пользователь замещает (для блока «Вы замещаете»). */
    static async getReplacing(): Promise<AxiosResponse<IReplacingPerson[]>> {
        return $api.get<IReplacingPerson[]>(`/user/replacing`);
    }

    static async rejectActivation(formData: IUsersManagementData): Promise<AxiosResponse> {
        return $api.post(`/user/RejectActivation`, formData);
    }

    static async fetchApprovalAssistants(controller: AbortController): Promise<AxiosResponse<IApprovalAssistantsData>> {
        return $api.get<IApprovalAssistantsData>(`/user/ApprovalAssistants`, { signal: controller.signal });
    }

    static async saveApprovalAssistants(formData: IChangeApprovalAssistantsData): Promise<AxiosResponse> {
        return $api.post(`/user/ApprovalAssistants`, formData);
    }

    static async deleteAccount(): Promise<AxiosResponse> {
        return $api.post(`/user/DeleteAccount`);
    }
}
